import pick from 'lodash/pick';

import {getTooltipDataForProperty} from 'common/widgets/context-panel/util';

import {registerMPElement} from '../../../../util/register-element';
import {PropertyType} from '../../../property-filter-menu/models/types';
import {STRING_TYPECAST_PROPERTIES} from '../../../property-filter-menu/models/utils';

import {BuilderPaneBase} from '../../common-components/builder-pane-base';
import {SHOW_MORE_LIMIT} from '../../models/constants';
import {DISABLED_DUE_TO_SENSITIVE_DATA} from 'common/report/constants';
import {PropertyTab} from '../../models/enums/builder-enums';
import {Property} from '../../models/enums/expression-enums';

import {
  createPrimitiveTabData,
  createDataGroupTabData,
  getIconForPropertyType,
  isDefinedValue,
  isCohortLiteral,
  isEventLiteral,
  renameProperty,
} from '../../util';

import '../builder-pane-filter-property';
import template from './index.jade';
import css from './index.styl?inline';

export default registerMPElement(
  `cohorts-builder-property-filter`,
  class PropertyFilterBuilderPane extends BuilderPaneBase {
    static get attrsSchema() {
      return {
        ...super.attrsSchema,
        'pane-visible': `boolean`,
      };
    }

    shouldUpdate(state) {
      if (!state.stagedCohort) {
        return false;
      }

      const literal = this.getLiteral(state);
      return (
        (isEventLiteral(literal) || isCohortLiteral(literal)) &&
        isDefinedValue(state[this.builderStateReference].propertyFilterIdx)
      );
    }

    get config() {
      return {
        ...super.config,
        useShadowDom: true,
        css,
        template,
        helpers: {
          ...super.config.helpers,
          pick,
          PropertyTab,
          clickedItem: (ev) => {
            const {item} = ev.detail;
            const {name, propertyType, dataGroupId, customPropertyId} = item;
            const type = STRING_TYPECAST_PROPERTIES.includes(name) ? PropertyType.String : item.type;

            this.dispatchBubbledEvent(`selected-property`, {
              name,
              type,
              propertyType,
              dataGroupId,
              customPropertyId,
            });

            if (type === PropertyType.Dimension) {
              this.app.updateBuilderState({
                searchFilter: ``,
                stagedDimension: item,
              });
              // Recurse screens for dimension
              this.nextPane(`cohorts-builder-property-filter`);
            } else {
              this.app.updateBuilderState({
                stagedDimension: null,
              });
              this.nextPane(`cohorts-builder-pane-filter-property`);
            }
          },
          showItemDetails: (ev) => this.showItemDetails(ev.detail.item),
          handlePropertyTabToggleChange: (ev) => {
            const selectedTab = this.helpers.getPropertyTabs().find((tab) => tab.value === ev.detail.selected);
            this.updateBuilder({propertyTab: ev.detail.selected, propertyTabType: selectedTab.type});
          },
          hasPropertyTabToggle: () => {
            return this.helpers.getPropertyTabs().length > 1;
          },
          getPropertyTabs: () => {
            const literal = this.getLiteral();
            const isAnalyzingUser = this.app.isAnalyzingUser();
            if (isEventLiteral(literal)) {
              return [createPrimitiveTabData(PropertyTab.Events)];
            } else if (isCohortLiteral(literal)) {
              return [
                ...(this.app.hasMultiplePeopleGroups() ? [createPrimitiveTabData(PropertyTab.All)] : []),
                ...(isAnalyzingUser ? [createPrimitiveTabData(PropertyTab.People)] : []),
                ...(this.app.hasDataGroups()
                  ? this.state.dataGroups.map((dataGroup) => createDataGroupTabData(dataGroup))
                  : []),
              ];
            } else {
              throw new Error(`Unexpected literal type`);
            }
          },
          getSections: () => {
            const {propertyTabType, propertyTab} = this.builderState;
            switch (propertyTabType) {
              case PropertyTab.All:
                return this.helpers
                  .getPropertyTabs()
                  .filter((tab) => tab.type !== PropertyTab.All)
                  .map((tab) => this.getTabSection(tab.type, tab.value));
              default:
                return [this.getTabSection(propertyTabType, propertyTab)];
            }
          },
        },
      };
    }

    showItemDetails(item) {
      let tooltipData = null;
      const {propertyType, dataGroupId} = item;
      // Don't show lexicon tooltip when it's a joined datagroup property or if there's a global datagroup selected
      if (!dataGroupId && !this.app.getDataGroupId()) {
        let propertyDefinitions = null;
        if (propertyType === Property.Event) {
          propertyDefinitions = this.state.eventPropertyDefinitions;
        } else {
          propertyDefinitions = this.state.userPropertyDefinitions;
        }
        const mpProperty = pick(item, [`name`, `customPropertyId`, `customProperty`]);
        if (mpProperty.customPropertyId || mpProperty.customProperty) {
          mpProperty.actionDisabled = true;
        }

        tooltipData = getTooltipDataForProperty(mpProperty, propertyDefinitions);
      }
      this.update({tooltipData});
    }

    getSelectedPropertyName() {
      const {propertyFilterIdx} = this.builderState;
      return this.state.stagedCohort.getPropertyName(this.state.stagedClausePosition, propertyFilterIdx);
    }

    getSelectedDataGroup(dataGroupId) {
      return this.state.dataGroups.find((dataGroup) => dataGroup.groupID === dataGroupId);
    }

    getEventsSection() {
      const selectedPropertyName = this.getSelectedPropertyName();
      return {
        isLoading: this.state.loading.eventProperties,
        items: this.getPropertyItems(this.state.eventProperties || [], Property.Event, selectedPropertyName),
        label: `Event Properties`,
        showLimit: SHOW_MORE_LIMIT,
      };
    }

    getDataGroupSection(selectedDataGroup) {
      return {
        items: this.getPropertyItems(
          selectedDataGroup.properties || [],
          Property.User,
          this.getSelectedPropertyName(),
          selectedDataGroup.groupID,
        ),
        label: selectedDataGroup.displayName,
        showLimit: SHOW_MORE_LIMIT,
      };
    }

    getDimensionSection() {
      const selectedPropertyName = this.getSelectedPropertyName();
      return {
        isLoading: this.state.loading.dimensions,
        items: this.getPropertyItems(
          this.state.dimensionProperties || [],
          this.builderState.stagedDimension.propertyType,
          selectedPropertyName,
        ),
        label: `${this.builderState.stagedDimension.name}.`,
        showLimit: SHOW_MORE_LIMIT,
      };
    }

    getPeopleSection() {
      const selectedPropertyName = this.getSelectedPropertyName();
      return {
        isLoading: this.state.loading.peopleProperties,
        items: this.getPropertyItems(this.state.peopleProperties || [], Property.User, selectedPropertyName),
        label: `Profile Properties`,
        showLimit: SHOW_MORE_LIMIT,
      };
    }

    getTabSection(type, value) {
      if (this.builderState.stagedDimension) {
        return this.getDimensionSection();
      }

      switch (type) {
        case PropertyTab.Events:
          return this.getEventsSection();
        case PropertyTab.People:
          return this.getPeopleSection();
        case PropertyTab.DataGroup:
          return this.getDataGroupSection(this.getSelectedDataGroup(value));
      }
    }

    getLiteral(state = this.state) {
      const {literalIdx} = state.stagedClausePosition;
      return state.stagedCohort.literals[literalIdx];
    }

    getPropertyItems(properties, propertyType, selectedProperty, dataGroupId = null) {
      return properties.map((ev) => ({
        propertyType,
        label: renameProperty(ev.name, {dataGroupId}),
        icon: getIconForPropertyType(ev.type),
        hasCaret: !ev.sensitive,
        isDisabled: ev.sensitive,
        tooltip: ev.sensitive ? {text: DISABLED_DUE_TO_SENSITIVE_DATA} : null,
        isSelected: ev.name === selectedProperty,
        dataGroupId,
        ...ev,
      }));
    }
  },
);
